import { API_ENDPOINTS } from '@common/constants/apiEndpoint';
import { SigninTorusParams } from '@common/types';
import { formatScreens } from '@common/utils';
import restClient from '@common/configs/api';
import io from './feathers.io';
import {
  FindCollectionsRequestParams,
  FindCollectionsResult,
  GetSharedAppResult,
} from 'click-types';

/**
 * Bootstrap reads (app + screens) go over REST instead of socket.io.
 *
 * These are public share GETs. On flaky mobile networks a socket.io connection
 * can fail to establish (e.g. WebSocket blocked by the carrier), which made
 * these calls queue and time out after 30s with NO server-side log. REST is a
 * plain stateless HTTPS request (gzip + CDN friendly) and is far more reliable
 * for the initial load. socket.io is still used for realtime data afterwards.
 */
const REST_TIMEOUT_MS = 45000;
const REST_RETRIES = 2;

const restGetWithRetry = async <T>(
  url: string,
  params?: Record<string, any>
): Promise<T> => {
  let lastError: unknown;
  for (let attempt = 0; attempt <= REST_RETRIES; attempt++) {
    try {
      const res = await restClient.get<T>(url, {
        params,
        timeout: REST_TIMEOUT_MS,
      });
      return res.data;
    } catch (error) {
      lastError = error;
      if (attempt < REST_RETRIES) {
        await new Promise((resolve) =>
          setTimeout(resolve, 2000 * (attempt + 1))
        );
      }
    }
  }
  throw lastError;
};

// App services
export const fetchApp = async (appId: string) => {
  const res = await restGetWithRetry<GetSharedAppResult>(
    `${API_ENDPOINTS.APP}/${appId}`
  );
  return { ...res, metadata: JSON.parse(res.metadata) };
};

export const fetchCollections = async (
  appId: string
): Promise<FindCollectionsResult | undefined> => {
  const findCollectionsRequestParams: FindCollectionsRequestParams = {
    appId,
  };

  const res: FindCollectionsResult = await io
    .service(API_ENDPOINTS.COLLECTIONS)
    .find({
      query: findCollectionsRequestParams,
    });

  return res;
};

// Page services
export const fetchPages = async (
  appId: string,
  params: Record<string, any>
) => {
  const res = await restGetWithRetry<{ data: any; expiresOn: any }>(
    `/v2/share/${appId}/screens`,
    {
      ...params,
      appId,
    }
  );

  return {
    data: formatScreens({ screens: res?.data }).list().toView(),
    expiresOn: res?.expiresOn,
  };
};

export const fetchTemplateById = async (
  appId: string,
  pageTemplateId: string
) => {
  try {
    const res = await io
      .service(API_ENDPOINTS.V3_PAGE_TEMPLATE)
      .get(pageTemplateId, {
        query: {
          appId,
        },
      });

    return res;
  } catch (e) {
    console.error(e);
  }
};

export const fetchBrandAssets = async (appId: string) => {
  try {
    const res = await io.service(API_ENDPOINTS.APP_STYLES).find({
      query: {
        appId,
      },
    });

    const brandAssetsMap: Record<string, any> = {};
    if (res.data) {
      res.data.forEach(
        (item: {
          id: string;
          value: any;
          screenType: any;
          category: string;
          type: string;
        }) => {
          let value = item.value;
          if (item.type === 'text') {
            //remove color from values
            delete value?.color;
          }

          if (!(item.category in brandAssetsMap)) {
            brandAssetsMap[item.category] = {
              [item.screenType]: {
                id: item.id,
                value,
              },
            };
          } else {
            brandAssetsMap[item.category] = {
              ...brandAssetsMap[item.category],
              [item.screenType]: { id: item.id, value },
            };
          }
        }
      );
    }

    return brandAssetsMap;
  } catch (e) {
    console.error(e);
  }
};

//TORUS
export const signinTorus = async (data: SigninTorusParams, appId: string) => {
  const { userPreview, accessToken } = await io.authenticate(
    { ...data, strategy: 'torus' },
    {
      query: {
        appId: appId,
      },
    }
  );

  return Object.assign(userPreview, { accessToken });
};

/**
 * Buckets whose objects are resolved through a pre-signed URL.
 *
 * File security used to be opt-in — gated behind a USE_FILE_SECURITY constant
 * and a per-field `fileSecurity` toggle on the canvas — and was documented as
 * being for platio-canvas only. It is now the default for every image, so both
 * switches are gone.
 *
 * `asteria-prod.platio-canvas.dbfile` was missing from this list, which meant
 * a stored full URL for the asteria stack fell through to the `startsWith('http')`
 * check in isS3Path and was never recognised. The one product the feature was
 * written for was the one it silently skipped.
 */
const S3_BUCKETS = [
  'asteria-prod.click-platform.dbfile',
  'asteria-prod.platio-canvas.dbfile',
  'dev.click-platform.dbfile',
  'staging.click-platform.dbfile',
];
/**
 * ex: "workspaces/ee5e3a29-f469-42f1-a455-fae3cb96ae4e/2026/04/17/user37187/circle-ci-icon-short.jpeg"
 * ex: "user37187/2026/04/17/circle-ci-icon-short.jpeg"
 * ex: "https://s3.ap-northeast-1.amazonaws.com/staging.click-platform.dbfile/user40221/2025/12/25/%E3%82%B9%E3%82%AF%E3%83%AA%E3%83%BC%E3%83%B3%E3%82%B7%E3%83%A7%E3%83%83%E3%83%88%202025-12-25%2013.50.09.png"
 * @param path
 * @returns pathObject ex: "workspaces/ee5e3a29-f469-42f1-a455-fae3cb96ae4e/2026/04/17/user37187/circle-ci-icon-short.jpeg"
 */
const getPathFromS3Url = (url: string): string => {
  const uri = url.split('?')[0];
  let path = uri.replace(/^https?:\/\/[^/]+\//, '');
  for (const bucket of S3_BUCKETS) {
    if (path.startsWith(`${bucket}/`)) {
      path = path.slice(bucket.length + 1);
      break;
    }
  }
  // The pathname segment in an S3 URL is percent-encoded (e.g. "β" → "%CE%B2").
  // Return the raw S3 key here — the HTTP layer re-encodes it when sent as the
  // `pathObject` query parameter. Without this decode, the key arrives at the
  // backend as the literal "%CE%B2…" and S3 signs a non-existent key.
  try {
    return decodeURIComponent(path);
  } catch {
    return path;
  }
};

const isS3Path = (path: string): boolean => {
  if (S3_BUCKETS.some((bucket) => path.includes(bucket))) {
    return true;
  }
  return !path.startsWith('http') && !path.startsWith('data');
};
/**
 * ex: "workspaces/ee5e3a29-f469-42f1-a455-fae3cb96ae4e/2026/04/17/user37187/circle-ci-icon-short.jpeg"
 * ex: "user37187/2026/04/17/circle-ci-icon-short.jpeg"
 * ex: "https://s3.ap-northeast-1.amazonaws.com/staging.click-platform.dbfile/user37187/2026/04/17/circle-ci-icon-short.jpeg"
 * @param path
 * @returns pathObject signed
 */
export const fetchSecurityImage = async (path: string): Promise<string> => {
  const isS3 = isS3Path(path);
  if (!isS3) {
    return path;
  }
  const pathObject = getPathFromS3Url(path);
  const { url } = await io.service(API_ENDPOINTS.SHARE_FILES).find({
    query: {
      pathObject,
      expiresIn: 300, // 300 seconds = 5 minutes
    },
  });
  return url;
};
